import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Menu, X, CreditCard } from "lucide-react";
import { scrollToId, EASE } from "./anim";
import { PAYMENT_URL, PAYMENT_LABEL } from "@/lib/config";

const LINKS = [
  { label: "Who We Help", id: "who-we-help" },
  { label: "What We Do", id: "what-we-do" },
  { label: "Founder", id: "founder" },
  { label: "Process", id: "process" },
  { label: "FAQ", id: "faq" },
];

export default function Nav({ onBook }) {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const go = (id) => {
    setOpen(false);
    scrollToId(id);
  };

  return (
    <header
      data-testid="site-nav"
      className={`fixed inset-x-0 top-0 z-50 transition-[background-color,border-color,backdrop-filter] duration-500 ${
        scrolled ? "border-b border-white/10 bg-[#08080A]/85 backdrop-blur-xl" : "bg-transparent"
      }`}
    >
      <div className="mx-auto flex h-16 max-w-7xl items-center justify-between px-5 sm:px-8">
        <button
          data-testid="nav-logo"
          onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
          className="flex items-center gap-3 text-left"
        >
          <img
            src="/assets/logo.jpeg"
            alt="Kingdom Wealth Enterprises logo"
            data-testid="nav-logo-img"
            className="h-10 w-10 rounded-full border border-[#C5A059]/40 object-cover object-top"
          />
          <span>
            <span className="block text-[13px] font-semibold uppercase tracking-[0.3em] text-[#F9F8F6]">
              Kingdom Wealth
            </span>
            <span className="block text-[9px] font-medium uppercase tracking-[0.42em] text-[#C5A059]">
              Enterprises, LLC
            </span>
          </span>
        </button>

        <nav className="hidden items-center gap-8 lg:flex" aria-label="Primary">
          {LINKS.map((l) => (
            <button
              key={l.id}
              data-testid={`nav-link-${l.id}`}
              onClick={() => go(l.id)}
              className="text-[11px] font-medium uppercase tracking-[0.22em] text-[#A1A1AA] transition-colors duration-300 hover:text-[#C5A059]"
            >
              {l.label}
            </button>
          ))}
          {PAYMENT_URL && (
            <a
              data-testid="nav-pay-link"
              href={PAYMENT_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-[0.22em] text-[#C5A059] transition-colors duration-300 hover:text-[#D4AF37]"
            >
              <CreditCard size={13} /> {PAYMENT_LABEL}
            </a>
          )}
          <button data-testid="nav-book-button" onClick={onBook} className="btn-gold !px-6 !py-2.5 text-xs">
            Schedule a Consultation
          </button>
        </nav>

        <button
          data-testid="nav-mobile-toggle"
          className="text-[#F9F8F6] lg:hidden"
          onClick={() => setOpen((v) => !v)}
          aria-label={open ? "Close menu" : "Open menu"}
          aria-expanded={open}
        >
          {open ? <X size={22} /> : <Menu size={22} />}
        </button>
      </div>

      <AnimatePresence>
        {open && (
          <motion.nav
            data-testid="nav-mobile-menu"
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.4, ease: EASE }}
            className="overflow-hidden border-b border-white/10 bg-[#08080A]/95 backdrop-blur-xl lg:hidden"
            aria-label="Mobile"
          >
            <div className="flex flex-col gap-1 px-5 py-4">
              {LINKS.map((l) => (
                <button
                  key={l.id}
                  data-testid={`nav-mobile-link-${l.id}`}
                  onClick={() => go(l.id)}
                  className="py-3 text-left text-sm font-medium uppercase tracking-[0.2em] text-[#A1A1AA] transition-colors hover:text-[#C5A059]"
                >
                  {l.label}
                </button>
              ))}
              {PAYMENT_URL && (
                <a
                  data-testid="nav-mobile-pay-link"
                  href={PAYMENT_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-2 py-3 text-sm font-semibold uppercase tracking-[0.2em] text-[#C5A059]"
                >
                  <CreditCard size={15} /> {PAYMENT_LABEL}
                </a>
              )}
              <button
                data-testid="nav-mobile-book-button"
                onClick={() => {
                  setOpen(false);
                  onBook();
                }}
                className="btn-gold mt-3"
              >
                Schedule a Consultation
              </button>
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </header>
  );
}
